import type {
  WorkflowBuilderState,
  WorkflowEntity,
  WorkflowRelationship,
  WorkflowRelationshipMapping,
  UnstructuredRelationship,
  LinkNodesStep,
  EnrichRule,
  IngestionStepOptions,
} from '@/interfaces/workflows.interface';

export const workflowInitialState: WorkflowBuilderState = {
  name: '',
  description: '',
  stepOptions: {
    structured: {
      mergeNodes: true,
      onDuplicate: 'update',
      batchSize: 0,
      vectorize: false,
      encoding: 'utf-8',
    },
    unstructured: {
      mergeNodes: true,
      onDuplicate: 'update',
      batchSize: 0,
      vectorize: true,
      encoding: 'utf-8',
    },
  },
  entities: [],
  relationships: [],
  relationshipMappings: [],
  unstructuredRelationships: [],
  linkNodesConfigurations: [],
  enrichRules: [],
  cypherClassifierSteps: [],
  currentStep: 0,
  isComplete: false,
};

export type WorkflowsAction =
  | { type: 'ADD_ENTITY'; payload: WorkflowEntity }
  | {
      type: 'UPDATE_ENTITY';
      payload: { entityId: string; updates: Partial<WorkflowEntity> };
    }
  | { type: 'REMOVE_ENTITY'; payload: string }
  | { type: 'ADD_RELATIONSHIP'; payload: WorkflowRelationship }
  | {
      type: 'UPDATE_RELATIONSHIP';
      payload: {
        relationshipId: string;
        updates: Partial<WorkflowRelationship>;
      };
    }
  | { type: 'REMOVE_RELATIONSHIP'; payload: string }
  | { type: 'ADD_RELATIONSHIP_MAPPING'; payload: WorkflowRelationshipMapping }
  | {
      type: 'UPDATE_RELATIONSHIP_MAPPING';
      payload: {
        mappingId: string;
        updates: Partial<WorkflowRelationshipMapping>;
      };
    }
  | { type: 'REMOVE_RELATIONSHIP_MAPPING'; payload: string }
  | { type: 'ADD_UNSTRUCTURED_RELATIONSHIP'; payload: UnstructuredRelationship }
  | {
      type: 'UPDATE_UNSTRUCTURED_RELATIONSHIP';
      payload: {
        relationshipId: string;
        updates: Partial<UnstructuredRelationship>;
      };
    }
  | { type: 'REMOVE_UNSTRUCTURED_RELATIONSHIP'; payload: string }
  | { type: 'ADD_LINK_NODES_CONFIGURATION'; payload: LinkNodesStep }
  | {
      type: 'UPDATE_LINK_NODES_CONFIGURATION';
      payload: {
        configurationId: string;
        updates: Partial<LinkNodesStep>;
      };
    }
  | { type: 'REMOVE_LINK_NODES_CONFIGURATION'; payload: string }
  | { type: 'ADD_ENRICH_RULE'; payload: EnrichRule }
  | { type: 'UPDATE_ENRICH_RULE'; payload: { index: number; rule: EnrichRule } }
  | { type: 'REMOVE_ENRICH_RULE'; payload: number }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | {
      type: 'UPDATE_STEP_OPTIONS';
      payload: {
        structured?: IngestionStepOptions;
        unstructured?: IngestionStepOptions;
      };
    }
  | { type: 'RESET_WORKFLOW' }
  | { type: 'LOAD_WORKFLOW'; payload: WorkflowBuilderState };

export const workflowsReducer = (
  state: WorkflowBuilderState,
  action: WorkflowsAction
): WorkflowBuilderState => {
  let newState: WorkflowBuilderState;

  switch (action.type) {
    case 'ADD_ENTITY':
      newState = {
        ...state,
        entities: [...state.entities, action.payload],
      };

      return newState;
    case 'UPDATE_ENTITY':
      newState = {
        ...state,
        entities: state.entities.map((entity) =>
          entity.id === action.payload.entityId
            ? { ...entity, ...action.payload.updates }
            : entity
        ),
      };

      return newState;
    case 'REMOVE_ENTITY':
      newState = {
        ...state,
        entities: state.entities.filter(
          (entity) => entity.id !== action.payload
        ),
      };

      return newState;
    case 'ADD_RELATIONSHIP':
      return {
        ...state,
        relationships: [...state.relationships, action.payload],
      };
    case 'UPDATE_RELATIONSHIP':
      return {
        ...state,
        relationships: state.relationships.map((relationship) =>
          relationship.id === action.payload.relationshipId
            ? { ...relationship, ...action.payload.updates }
            : relationship
        ),
      };
    case 'REMOVE_RELATIONSHIP':
      return {
        ...state,
        relationships: state.relationships.filter(
          (relationship) => relationship.id !== action.payload
        ),
      };
    case 'ADD_RELATIONSHIP_MAPPING':
      return {
        ...state,
        relationshipMappings: [...state.relationshipMappings, action.payload],
      };
    case 'UPDATE_RELATIONSHIP_MAPPING':
      return {
        ...state,
        relationshipMappings: state.relationshipMappings.map((mapping) =>
          mapping.id === action.payload.mappingId
            ? { ...mapping, ...action.payload.updates }
            : mapping
        ),
      };
    case 'REMOVE_RELATIONSHIP_MAPPING':
      return {
        ...state,
        relationshipMappings: state.relationshipMappings.filter(
          (mapping) => mapping.id !== action.payload
        ),
      };
    case 'ADD_UNSTRUCTURED_RELATIONSHIP':
      return {
        ...state,
        unstructuredRelationships: [
          ...state.unstructuredRelationships,
          action.payload,
        ],
      };
    case 'UPDATE_UNSTRUCTURED_RELATIONSHIP':
      return {
        ...state,
        unstructuredRelationships: state.unstructuredRelationships.map(
          (relationship) =>
            relationship.id === action.payload.relationshipId
              ? { ...relationship, ...action.payload.updates }
              : relationship
        ),
      };
    case 'REMOVE_UNSTRUCTURED_RELATIONSHIP':
      return {
        ...state,
        unstructuredRelationships: state.unstructuredRelationships.filter(
          (relationship) => relationship.id !== action.payload
        ),
      };
    case 'ADD_LINK_NODES_CONFIGURATION':
      return {
        ...state,
        linkNodesConfigurations: [
          ...state.linkNodesConfigurations,
          action.payload,
        ],
      };
    case 'UPDATE_LINK_NODES_CONFIGURATION':
      return {
        ...state,
        linkNodesConfigurations: state.linkNodesConfigurations.map((config) =>
          config.id === action.payload.configurationId
            ? { ...config, ...action.payload.updates }
            : config
        ),
      };
    case 'REMOVE_LINK_NODES_CONFIGURATION':
      return {
        ...state,
        linkNodesConfigurations: state.linkNodesConfigurations.filter(
          (config) => config.id !== action.payload
        ),
      };
    case 'ADD_ENRICH_RULE':
      return {
        ...state,
        enrichRules: [...state.enrichRules, action.payload],
      };
    case 'UPDATE_ENRICH_RULE':
      return {
        ...state,
        enrichRules: state.enrichRules.map((rule, index) =>
          index === action.payload.index ? action.payload.rule : rule
        ),
      };
    case 'REMOVE_ENRICH_RULE':
      return {
        ...state,
        enrichRules: state.enrichRules.filter(
          (_, index) => index !== action.payload
        ),
      };
    case 'SET_CURRENT_STEP':
      return {
        ...state,
        currentStep: action.payload,
      };
    case 'UPDATE_STEP_OPTIONS':
      return {
        ...state,
        stepOptions: { ...state.stepOptions, ...action.payload },
      };
    case 'RESET_WORKFLOW':
      return workflowInitialState;
    case 'LOAD_WORKFLOW':
      return action.payload;
    default:
      return state;
  }
};
